<template>
  <v-container fluid class="costos-reconocimiento-root">
    <v-card tile>
      <div class="pl-6">
        <p class="text-body-1 font-weight-regular pt-4">
          Control de Costos AWS — Canal Web Reconocimiento
        </p>
        <p
          class="text-caption text--secondary font-weight-bold mt-n3 pb-4"
          v-if="selectedFecha && selectedFecha[0]"
        >
          <v-icon color="black">mdi-calendar-range</v-icon>
          del {{ selectedFecha[0] }} al {{ selectedFecha[1] }}
        </p>
      </div>
    </v-card>

    <!-- Selector de rango de fechas -->
    <v-row class="mt-2">
      <v-col cols="12" sm="4" md="3">
        <v-menu
          ref="fechaDialog"
          v-model="modalFechas"
          :close-on-content-click="false"
          transition="scale-transition"
          offset-y
          min-width="auto"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              :value="selectedFechaTexto"
              readonly
              dense
              hide-details
              outlined
              label="Rango de fechas"
              prepend-icon="mdi-calendar"
              v-bind="attrs"
              v-on="on"
            ></v-text-field>
          </template>
          <v-date-picker
            locale="es"
            width="240"
            no-title
            scrollable
            v-model="dialogVarSelectedFecha"
            range
            :max="disabledFecha"
            color="primary"
            header-color="primary"
          >
            <v-spacer></v-spacer>
            <v-btn text color="primary" @click="modalFechas = false"
              >Cancelar</v-btn
            >
            <v-btn text color="primary" @click="fnAplicarRango">OK</v-btn>
          </v-date-picker>
        </v-menu>
      </v-col>
    </v-row>

    <!-- Banner de limitación -->
    <v-alert type="warning" outlined dense class="mt-2">
      Costo AWS estimado <strong>mínimo</strong>: asume 1 intento exitoso por
      gestión (no existe registro de reintentos de foto/verificación de vida) y
      solo cuenta el perfil completo de 9 llamadas AWS en gestiones
      <strong>completadas</strong> — no incluye llamadas reales que ya ocurren
      en gestiones abandonadas (fachada, recibo, DPI) ni códigos OTP enviados
      pero nunca verificados.
    </v-alert>

    <!-- KPIs -->
    <v-row v-if="!cargandoKpis">
      <v-col cols="6" sm="4" md="2">
        <v-card outlined class="pa-3 fill-height">
          <div class="text-caption text--secondary">Gestiones iniciadas</div>
          <div class="text-h5 font-weight-medium">
            {{ fnFormatNumero(kpis.gestionesIniciadas) }}
          </div>
        </v-card>
      </v-col>
      <v-col cols="6" sm="4" md="2">
        <v-card outlined class="pa-3 fill-height">
          <div class="text-caption text--secondary">Gestiones completadas</div>
          <div class="text-h5 font-weight-medium">
            {{ fnFormatNumero(kpis.gestionesCompletadas) }}
          </div>
        </v-card>
      </v-col>
      <v-col cols="6" sm="4" md="2">
        <v-card outlined class="pa-3 fill-height">
          <div class="text-caption text--secondary">Gestiones abandonadas</div>
          <div class="text-h5 font-weight-medium">
            {{ fnFormatNumero(kpis.gestionesAbandonadas) }}
          </div>
        </v-card>
      </v-col>
      <v-col cols="6" sm="4" md="2">
        <v-card outlined class="pa-3 fill-height">
          <div class="text-caption text--secondary">
            Costo AWS estimado (mes)
          </div>
          <div class="text-h5 font-weight-medium">
            {{ fnFormatMoneda(costoAws.totalUSD, 'USD') }}
          </div>
        </v-card>
      </v-col>
      <v-col cols="6" sm="4" md="2">
        <v-card outlined class="pa-3 fill-height">
          <div class="text-caption text--secondary">
            Costo promedio / gestión
          </div>
          <div class="text-h5 font-weight-medium">
            {{ fnFormatMoneda(costoAws.costoPromedioPorGestionUSD, 'USD', 4) }}
          </div>
        </v-card>
      </v-col>
      <v-col cols="6" sm="4" md="2">
        <v-card outlined class="pa-3 fill-height">
          <div class="text-caption text--secondary">Tipo de cambio ref.</div>
          <div class="text-h5 font-weight-medium">Q {{ tipoCambio }}</div>
        </v-card>
      </v-col>
    </v-row>
    <v-row v-else
      ><v-col cols="12" class="text-center pa-8"
        ><v-progress-circular
          indeterminate
          color="primary"
        ></v-progress-circular></v-col
    ></v-row>

    <!-- Gráficas -->
    <v-row class="mt-2">
      <v-col cols="12" md="4">
        <v-card outlined class="pa-3">
          <div class="text-subtitle-2 mb-2">
            Mix de canal OTP (gestiones completadas del periodo)
          </div>
          <canvas ref="canvasDona" height="220"></canvas>
        </v-card>
      </v-col>
      <v-col cols="12" md="4">
        <v-card outlined class="pa-3">
          <div class="text-subtitle-2 mb-2">Gestiones completadas por mes</div>
          <canvas ref="canvasTendenciaGestiones" height="220"></canvas>
        </v-card>
      </v-col>
      <v-col cols="12" md="4">
        <v-card outlined class="pa-3">
          <div class="text-subtitle-2 mb-2">
            Costo AWS estimado por mes (USD)
          </div>
          <canvas ref="canvasTendenciaCosto" height="220"></canvas>
        </v-card>
      </v-col>
    </v-row>

    <!-- Tabla de tendencia (vista de datos, accesible) -->
    <v-row>
      <v-col cols="12">
        <v-data-table
          dense
          :headers="headersTendencia"
          :items="tendenciaMensual"
          disable-pagination
          hide-default-footer
          class="elevation-1"
        >
          <template v-slot:[`item.costoUSD`]="{ item }">{{
            fnFormatMoneda(item.costoUSD, 'USD')
          }}</template>
        </v-data-table>
      </v-col>
    </v-row>

    <!-- Tabla comparativa de facturación -->
    <v-row class="mt-2">
      <v-col cols="12">
        <v-card outlined>
          <v-card-title class="text-subtitle-1"
            >Comparativo de modelos de cobro (periodo
            seleccionado)</v-card-title
          >
          <v-card-text>
            <p class="text-caption text--secondary">
              El PDF comercial cobra por evaluación
              <strong>iniciada</strong> (incluso abandonada) — se usa
              {{ fnFormatNumero(kpis.gestionesIniciadas) }} como volumen. El
              modelo interno calibra su tarifa sobre el perfil de 9 llamadas que
              solo corre completo en una gestión <strong>completada</strong> —
              se usa {{ fnFormatNumero(kpis.gestionesCompletadas) }} como
              volumen. Son bases de volumen distintas por diseño, no un error.
            </p>
            <v-simple-table>
              <thead>
                <tr>
                  <th>Modelo</th>
                  <th>Volumen aplicado</th>
                  <th>Tier / tramo</th>
                  <th>Monto a facturar</th>
                  <th>Costo AWS estimado</th>
                  <th>Margen</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="fila in filasPdf" :key="'pdf-' + fila.plan">
                  <td>
                    PDF comercial — {{ fila.plan }}
                    <v-chip
                      v-if="fila.esRecomendado"
                      x-small
                      color="primary"
                      dark
                      class="ml-1"
                      >más barato</v-chip
                    >
                  </td>
                  <td>
                    {{ fnFormatNumero(kpis.gestionesIniciadas) }} (límite
                    {{ fnFormatNumero(fila.limiteEvaluaciones) }})
                  </td>
                  <td>
                    {{
                      fila.excedente > 0
                        ? fnFormatNumero(fila.excedente) + ' excedentes'
                        : 'sin excedente'
                    }}
                  </td>
                  <td>
                    {{ fnFormatMoneda(fila.totalGTQ, 'GTQ') }} ({{
                      fnFormatMoneda(fila.totalUSD, 'USD')
                    }})
                  </td>
                  <td>{{ fnFormatMoneda(costoAws.totalUSD, 'USD') }}</td>
                  <td>
                    <v-chip
                      small
                      :color="
                        fila.totalUSD - costoAws.totalUSD >= 0 ? 'green' : 'red'
                      "
                      dark
                    >
                      {{
                        fnFormatMoneda(fila.totalUSD - costoAws.totalUSD, 'USD')
                      }}
                    </v-chip>
                  </td>
                </tr>
                <tr>
                  <td>Modelo interno</td>
                  <td>{{ fnFormatNumero(kpis.gestionesCompletadas) }}</td>
                  <td>
                    {{ facturaInterno.tramoAlcanzado }}
                    <v-chip
                      v-if="facturaInterno.requiereRevisionContrato"
                      x-small
                      color="red"
                      dark
                      class="ml-1"
                      >revisar contrato</v-chip
                    >
                  </td>
                  <td>
                    {{ fnFormatMoneda(facturaInterno.totalUSD, 'USD') }} ({{
                      fnFormatMoneda(facturaInterno.totalGTQ, 'GTQ')
                    }})
                  </td>
                  <td>{{ fnFormatMoneda(costoAws.totalUSD, 'USD') }}</td>
                  <td>
                    <v-chip
                      small
                      :color="
                        facturaInterno.totalUSD - costoAws.totalUSD >= 0
                          ? 'green'
                          : 'red'
                      "
                      dark
                    >
                      {{
                        fnFormatMoneda(
                          facturaInterno.totalUSD - costoAws.totalUSD,
                          'USD'
                        )
                      }}
                    </v-chip>
                  </td>
                </tr>
              </tbody>
            </v-simple-table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Fase 2b — Reconciliación con AWS Cost Explorer -->
    <v-row class="mt-2">
      <v-col cols="12">
        <v-card outlined>
          <v-card-title class="text-subtitle-1"
            >Reconciliación con AWS Cost Explorer — costo real facturado
            (periodo seleccionado)</v-card-title
          >
          <v-card-text>
            <div v-if="costoReal.cargando" class="text-center pa-4">
              <v-progress-circular
                indeterminate
                color="primary"
              ></v-progress-circular>
            </div>

            <v-alert
              v-else-if="costoReal.configurado === false"
              type="info"
              outlined
              dense
            >
              Reconciliación no configurada: {{ costoReal.mensaje }} Se necesita
              un usuario IAM con permiso <code>ce:GetCostAndUsage</code>
              (Cost Explorer debe estar activado en la cuenta AWS).
            </v-alert>

            <v-alert v-else-if="costoReal.error" type="warning" outlined dense>
              No se pudo consultar Cost Explorer: {{ costoReal.error }}
            </v-alert>

            <v-alert
              v-else-if="!costoReal.datosDisponibles"
              type="info"
              outlined
              dense
            >
              {{ costoReal.mensaje }}
            </v-alert>

            <template v-else>
              <v-alert
                v-if="migracionLlamadasPendiente"
                type="info"
                outlined
                dense
              >
                La tabla <code>reconocimiento_llamadas_aws</code> aún no está
                disponible (migración
                <code>phase-14-reconocimiento-llamadas-aws.sql</code> pendiente
                de correr) — la columna "Llamadas registradas" no tiene datos
                que comparar.
              </v-alert>

              <v-simple-table dense>
                <thead>
                  <tr>
                    <th>Operación AWS</th>
                    <th class="text-right">Llamadas registradas (backend)</th>
                    <th class="text-right">Llamadas facturadas (AWS)</th>
                    <th class="text-right">Costo estimado</th>
                    <th class="text-right">Costo real (AWS)</th>
                    <th class="text-right">Diferencia</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="fila in filasReconciliacion"
                    :key="fila.operacionAws"
                  >
                    <td>{{ fila.etiqueta }}</td>
                    <td class="text-right">
                      {{
                        fila.registradas === null
                          ? '—'
                          : fnFormatNumero(fila.registradas)
                      }}
                    </td>
                    <td class="text-right">
                      {{ fnFormatNumero(fila.facturadas) }}
                    </td>
                    <td class="text-right">
                      {{
                        fila.costoEstimadoUSD === null
                          ? '—'
                          : fnFormatMoneda(fila.costoEstimadoUSD, 'USD', 4)
                      }}
                    </td>
                    <td class="text-right">
                      {{ fnFormatMoneda(fila.costoRealUSD, 'USD', 4) }}
                    </td>
                    <td class="text-right">
                      <span v-if="fila.diferenciaUSD === null">—</span>
                      <v-chip
                        v-else
                        small
                        :color="fila.diferenciaUSD > 0.0001 ? 'red' : 'green'"
                        dark
                      >
                        {{ fnFormatMoneda(fila.diferenciaUSD, 'USD', 4) }}
                      </v-chip>
                    </td>
                  </tr>
                  <tr class="font-weight-bold">
                    <td>Total</td>
                    <td class="text-right">
                      {{
                        totalReconciliacion.registradas === null
                          ? '—'
                          : fnFormatNumero(totalReconciliacion.registradas)
                      }}
                    </td>
                    <td class="text-right">
                      {{ fnFormatNumero(totalReconciliacion.facturadas) }}
                    </td>
                    <td class="text-right">
                      {{
                        totalReconciliacion.costoEstimadoUSD === null
                          ? '—'
                          : fnFormatMoneda(
                              totalReconciliacion.costoEstimadoUSD,
                              'USD',
                              4
                            )
                      }}
                    </td>
                    <td class="text-right">
                      {{
                        fnFormatMoneda(
                          totalReconciliacion.costoRealUSD,
                          'USD',
                          4
                        )
                      }}
                    </td>
                    <td></td>
                  </tr>
                </tbody>
              </v-simple-table>

              <p class="text-caption text--secondary mt-3 mb-0">
                El check de Face Liveness se factura bajo
                <code>StartFaceLivenessSession</code> — la API de streaming que
                el navegador llama directo a AWS, sin pasar por el backend. Se
                compara contra las sesiones creadas
                (<code>CreateFaceLivenessSession</code>): si AWS factura más,
                son checks que el usuario inició y abandonó (igual se cobran).
                Cost Explorer tiene un rezago de hasta 24 horas y esta sección
                solo cubre Rekognition/Liveness (no OTP ni CloudWatch).
              </p>

              <div class="text-subtitle-2 mt-4 mb-2">
                Costo estimado vs facturado real por mes (USD, solo Rekognition
                + Face Liveness)
              </div>
              <v-row>
                <v-col cols="12" md="8">
                  <canvas ref="canvasCostoRealVsEstimado" height="220"></canvas>
                </v-col>
              </v-row>
            </template>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import moment from 'moment';
require('moment/locale/es.js');
import axios from 'axios';
import { mapActions } from 'vuex';
import {
  Chart,
  ArcElement,
  DoughnutController,
  BarElement,
  BarController,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import {
  TIPO_CAMBIO_GTQ_USD,
  COSTO_AWS,
} from '../../config/tarifasReconocimiento';
import {
  calcularCostoAwsEstimado,
  calcularFacturaPdfComercial,
  calcularFacturaModeloInterno,
} from '../../config/calculadorasCostosReconocimiento';

Chart.register(
  ArcElement,
  DoughnutController,
  BarElement,
  BarController,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend
);

// Paleta validada (skill dataviz — references/palette.md), sin modo oscuro: este panel no tiene tema oscuro.
const COLOR_CATEGORICO = {
  email: '#2a78d6',
  whatsapp: '#1baf7a',
  sms: '#eda100',
};
const COLOR_SUPERFICIE = '#fcfcfb';
const COLOR_TEXTO_SECUNDARIO = '#52514e';
const COLOR_MUTED = '#898781';
const COLOR_GRID = '#e1e0d9';

// Reconciliación Fase 2b: mapa operación facturable en Cost Explorer ↔ tipo_llamada
// registrado por el backend en reconocimiento_llamadas_aws. El liveness se factura
// bajo StartFaceLivenessSession (streaming navegador→AWS, no pasa por el backend),
// por eso se compara contra CreateFaceLivenessSession.
const OPERACIONES_RECONCILIACION = [
  {
    operacionAws: 'DetectText',
    etiqueta: 'DetectText (OCR de DPI y recibo)',
    tipoInterno: 'DetectText',
    tarifaUSD: COSTO_AWS.rekognitionImagenPorLlamada,
  },
  {
    operacionAws: 'DetectLabels',
    etiqueta: 'DetectLabels (fachada y recibo)',
    tipoInterno: 'DetectLabels',
    tarifaUSD: COSTO_AWS.rekognitionImagenPorLlamada,
  },
  {
    operacionAws: 'CompareFaces',
    etiqueta: 'CompareFaces (DPI vs selfie)',
    tipoInterno: 'CompareFaces',
    tarifaUSD: COSTO_AWS.rekognitionImagenPorLlamada,
  },
  {
    operacionAws: 'StartFaceLivenessSession',
    etiqueta: 'Face Liveness (check completo)',
    tipoInterno: 'CreateFaceLivenessSession',
    tarifaUSD: COSTO_AWS.faceLivenessPorCheck,
  },
];

export default {
  name: 'costosReconocimientoFacial',
  data() {
    return {
      cargandoKpis: true,
      tipoCambio: TIPO_CAMBIO_GTQ_USD,

      modalFechas: false,
      dialogVarSelectedFecha: [
        moment().startOf('month').format('YYYY-MM-DD'),
        moment().format('YYYY-MM-DD'),
      ],
      selectedFecha: [
        moment().startOf('month').format('DD-MM-YYYY'),
        moment().format('DD-MM-YYYY'),
      ],
      disabledFecha: moment().format('YYYY-MM-DD'),

      kpis: {
        gestionesIniciadas: 0,
        gestionesCompletadas: 0,
        gestionesAbandonadas: 0,
        otpPorCanal: { email: 0, whatsapp: 0, sms: 0 },
      },
      costoAws: {
        rekognitionUSD: 0,
        livenessUSD: 0,
        otpUSD: 0,
        cloudwatchUSD: 0,
        totalUSD: 0,
        costoPromedioPorGestionUSD: 0,
      },
      facturaPdf: { porTier: [], recomendado: null },
      facturaInterno: {
        tramoAlcanzado: 'base',
        totalUSD: 0,
        totalGTQ: 0,
        requiereRevisionContrato: false,
      },

      tendenciaMensual: [],
      headersTendencia: [
        { text: 'Mes', value: 'mesTexto', sortable: false },
        {
          text: 'Gestiones completadas',
          value: 'gestionesCompletadas',
          sortable: false,
        },
        { text: 'Costo AWS estimado', value: 'costoUSD', sortable: false },
      ],

      chartDona: null,
      chartTendenciaGestiones: null,
      chartTendenciaCosto: null,

      // Fase 2b — reconciliación con Cost Explorer
      costoReal: {
        cargando: true,
        configurado: null,
        datosDisponibles: true,
        error: null,
        mensaje: '',
        totalesPorOperacion: {},
        totalUSD: 0,
      },
      costoRealMensual: [],
      conteoLlamadas: {},
      migracionLlamadasPendiente: false,
      chartCostoRealVsEstimado: null,
    };
  },

  computed: {
    feathersServer() {
      return process.env.VUE_APP_FEATHERS_SERVER;
    },
    filasReconciliacion() {
      const porOperacion = this.costoReal.totalesPorOperacion || {};
      return OPERACIONES_RECONCILIACION.map((op) => {
        const real = porOperacion[op.operacionAws] || {
          costoUSD: 0,
          cantidad: 0,
        };
        const registradas =
          this.conteoLlamadas[op.tipoInterno] === undefined ||
          this.conteoLlamadas[op.tipoInterno] === null
            ? null
            : this.conteoLlamadas[op.tipoInterno];
        const costoEstimadoUSD =
          registradas === null ? null : registradas * op.tarifaUSD;
        return {
          operacionAws: op.operacionAws,
          etiqueta: op.etiqueta,
          registradas,
          facturadas: Math.round(real.cantidad),
          costoEstimadoUSD,
          costoRealUSD: real.costoUSD,
          diferenciaUSD:
            costoEstimadoUSD === null ? null : real.costoUSD - costoEstimadoUSD,
        };
      });
    },
    totalReconciliacion() {
      const filas = this.filasReconciliacion;
      const hayInternas = filas.every((f) => f.registradas !== null);
      return {
        registradas: hayInternas
          ? filas.reduce((acc, f) => acc + f.registradas, 0)
          : null,
        facturadas: filas.reduce((acc, f) => acc + f.facturadas, 0),
        costoEstimadoUSD: hayInternas
          ? filas.reduce((acc, f) => acc + f.costoEstimadoUSD, 0)
          : null,
        costoRealUSD: filas.reduce((acc, f) => acc + f.costoRealUSD, 0),
      };
    },
    selectedFechaTexto() {
      return this.selectedFecha && this.selectedFecha[0]
        ? `${this.selectedFecha[0]} — ${this.selectedFecha[1]}`
        : '';
    },
    filasPdf() {
      return this.facturaPdf.porTier.map((fila) => ({
        ...fila,
        esRecomendado:
          this.facturaPdf.recomendado &&
          fila.plan === this.facturaPdf.recomendado.plan,
      }));
    },
  },

  methods: {
    ...mapActions('GestionesReconocimientoFacial', { findGestiones: 'find' }),

    fnFormatNumero(n) {
      return Number(n || 0).toLocaleString('es-GT');
    },
    fnFormatMoneda(n, moneda, decimales = 2) {
      const simbolo = moneda === 'GTQ' ? 'Q' : '$';
      return `${simbolo}${Number(n || 0).toLocaleString('es-GT', {
        minimumFractionDigits: decimales,
        maximumFractionDigits: decimales,
      })}`;
    },

    fnRangoFechas() {
      const [s1, s2] = this.dialogVarSelectedFecha;
      return {
        $gte: moment(s1).startOf('day').toDate(),
        $lte: moment(s2).endOf('day').toDate(),
      };
    },

    fnAplicarRango() {
      this.selectedFecha = [
        moment(this.dialogVarSelectedFecha[0]).format('DD-MM-YYYY'),
        moment(this.dialogVarSelectedFecha[1]).format('DD-MM-YYYY'),
      ];
      this.modalFechas = false;
      this.cargarTodo();
    },

    async fnConteo(filtroExtra) {
      const query = {
        canal_de_creacion: 'web_reconocimiento',
        createdAt: this.fnRangoFechas(),
        ...filtroExtra,
        $limit: 1,
        $skip: 0,
      };
      const r = await this.findGestiones({ query });
      return r.total || 0;
    },

    async cargarKpis() {
      this.cargandoKpis = true;
      const [iniciadas, completadas, email, whatsapp, sms] = await Promise.all([
        this.fnConteo({}),
        this.fnConteo({ flag_precalificacion_completada: true }),
        this.fnConteo({ otp_canal_usado: 'email' }),
        this.fnConteo({ otp_canal_usado: 'whatsapp' }),
        this.fnConteo({ otp_canal_usado: 'sms' }),
      ]);

      this.kpis = {
        gestionesIniciadas: iniciadas,
        gestionesCompletadas: completadas,
        gestionesAbandonadas: Math.max(0, iniciadas - completadas),
        otpPorCanal: { email, whatsapp, sms },
      };

      this.costoAws = calcularCostoAwsEstimado({
        gestionesCompletadas: completadas,
        conteoOtpPorCanal: this.kpis.otpPorCanal,
      });
      this.facturaPdf = calcularFacturaPdfComercial(iniciadas);
      this.facturaInterno = calcularFacturaModeloInterno(completadas);

      this.cargandoKpis = false;
    },

    async cargarTendencia() {
      const desde = moment().subtract(11, 'months').startOf('month').toDate();
      const hasta = moment().endOf('month').toDate();

      const r = await this.findGestiones({
        query: {
          canal_de_creacion: 'web_reconocimiento',
          createdAt: { $gte: desde, $lte: hasta },
          $select: [
            'createdAt',
            'flag_precalificacion_completada',
            'otp_canal_usado',
          ],
          $limit: 20000,
          $skip: 0,
        },
      });

      const filas = r.data || [];
      const porMes = {};
      for (let i = 0; i < 12; i++) {
        const clave = moment()
          .subtract(11 - i, 'months')
          .format('YYYY-MM');
        porMes[clave] = {
          gestionesCompletadas: 0,
          otpPorCanal: { email: 0, whatsapp: 0, sms: 0 },
        };
      }

      filas.forEach((row) => {
        const clave = moment(row.createdAt).format('YYYY-MM');
        if (!porMes[clave]) return;
        if (row.flag_precalificacion_completada) {
          porMes[clave].gestionesCompletadas += 1;
          if (
            row.otp_canal_usado &&
            porMes[clave].otpPorCanal[row.otp_canal_usado] !== undefined
          ) {
            porMes[clave].otpPorCanal[row.otp_canal_usado] += 1;
          }
        }
      });

      this.tendenciaMensual = Object.keys(porMes)
        .sort()
        .map((clave) => {
          const datos = porMes[clave];
          const costo = calcularCostoAwsEstimado({
            gestionesCompletadas: datos.gestionesCompletadas,
            conteoOtpPorCanal: datos.otpPorCanal,
          });
          return {
            mes: clave,
            mesTexto: moment(clave, 'YYYY-MM').format('MMM YYYY'),
            gestionesCompletadas: datos.gestionesCompletadas,
            costoUSD: costo.totalUSD,
          };
        });

      this.renderGraficaTendencias();
    },

    renderGraficaDona() {
      if (this.chartDona) this.chartDona.destroy();
      const { email, whatsapp, sms } = this.kpis.otpPorCanal;
      const total = email + whatsapp + sms;
      this.chartDona = new Chart(this.$refs.canvasDona.getContext('2d'), {
        type: 'doughnut',
        data: {
          labels: ['Email', 'WhatsApp', 'SMS'],
          datasets: [
            {
              data: [email, whatsapp, sms],
              backgroundColor: [
                COLOR_CATEGORICO.email,
                COLOR_CATEGORICO.whatsapp,
                COLOR_CATEGORICO.sms,
              ],
              borderColor: COLOR_SUPERFICIE,
              borderWidth: 2,
            },
          ],
        },
        options: {
          cutout: '60%',
          plugins: {
            legend: {
              position: 'bottom',
              labels: { color: COLOR_TEXTO_SECUNDARIO },
            },
            datalabels: {
              color: '#fff',
              formatter: (value) =>
                total > 0 ? `${Math.round((value / total) * 100)}%` : '',
              display: (ctx) => ctx.dataset.data[ctx.dataIndex] > 0,
            },
          },
        },
        plugins: [ChartDataLabels],
      });
    },

    renderGraficaTendencias() {
      const labels = this.tendenciaMensual.map((m) => m.mesTexto);

      if (this.chartTendenciaGestiones) this.chartTendenciaGestiones.destroy();
      this.chartTendenciaGestiones = new Chart(
        this.$refs.canvasTendenciaGestiones.getContext('2d'),
        {
          type: 'bar',
          data: {
            labels,
            datasets: [
              {
                data: this.tendenciaMensual.map((m) => m.gestionesCompletadas),
                backgroundColor: COLOR_CATEGORICO.email,
                borderRadius: 4,
                maxBarThickness: 24,
              },
            ],
          },
          options: {
            plugins: { legend: { display: false } },
            scales: {
              x: { grid: { display: false }, ticks: { color: COLOR_MUTED } },
              y: {
                beginAtZero: true,
                grid: { color: COLOR_GRID, drawBorder: false },
                ticks: { color: COLOR_MUTED },
              },
            },
          },
        }
      );

      if (this.chartTendenciaCosto) this.chartTendenciaCosto.destroy();
      this.chartTendenciaCosto = new Chart(
        this.$refs.canvasTendenciaCosto.getContext('2d'),
        {
          type: 'bar',
          data: {
            labels,
            datasets: [
              {
                data: this.tendenciaMensual.map((m) => m.costoUSD),
                backgroundColor: COLOR_CATEGORICO.whatsapp,
                borderRadius: 4,
                maxBarThickness: 24,
              },
            ],
          },
          options: {
            plugins: { legend: { display: false } },
            scales: {
              x: { grid: { display: false }, ticks: { color: COLOR_MUTED } },
              y: {
                beginAtZero: true,
                grid: { color: COLOR_GRID, drawBorder: false },
                ticks: { color: COLOR_MUTED },
              },
            },
          },
        }
      );
    },

    fnAuthHeaders() {
      return {
        Authorization: 'Bearer ' + window.localStorage.getItem('feathers-jwt'),
      };
    },

    // Conteo de llamadas reales registradas por el backend (tabla
    // reconocimiento_llamadas_aws, Fase 2a) en el rango seleccionado.
    async fnConteoLlamadasAws(tipoLlamada) {
      const [s1, s2] = this.dialogVarSelectedFecha;
      const r = await axios.get(
        `${this.feathersServer}reconocimiento-llamadas-aws`,
        {
          params: {
            tipo_llamada: tipoLlamada,
            'createdAt[$gte]': moment(s1).startOf('day').toISOString(),
            'createdAt[$lte]': moment(s2).endOf('day').toISOString(),
            $limit: 1,
          },
          headers: this.fnAuthHeaders(),
          timeout: 30000,
        }
      );
      return r.data.total || 0;
    },

    async cargarCostoReal() {
      this.costoReal.cargando = true;
      this.costoReal.error = null;
      const [s1, s2] = this.dialogVarSelectedFecha;
      const url = `${this.feathersServer}reconocimiento-costos-aws-real`;

      try {
        // 1 llamada para el rango seleccionado (totales por operación) y 1 para
        // la tendencia de 12 meses; ambas cacheadas 12h en el backend.
        const [rango, mensual] = await Promise.all([
          axios.get(url, {
            params: { start: s1, end: s2, granularity: 'DAILY' },
            headers: this.fnAuthHeaders(),
            timeout: 60000,
          }),
          axios.get(url, {
            params: {
              start: moment()
                .subtract(11, 'months')
                .startOf('month')
                .format('YYYY-MM-DD'),
              end: moment().format('YYYY-MM-DD'),
              granularity: 'MONTHLY',
            },
            headers: this.fnAuthHeaders(),
            timeout: 60000,
          }),
        ]);

        this.costoReal.configurado = rango.data.configurado !== false;
        this.costoReal.datosDisponibles = rango.data.datosDisponibles !== false;
        this.costoReal.mensaje = rango.data.mensaje || '';
        if (!this.costoReal.configurado || !this.costoReal.datosDisponibles) {
          return;
        }

        this.costoReal.totalesPorOperacion =
          (rango.data.totales && rango.data.totales.porOperacion) || {};
        this.costoReal.totalUSD =
          (rango.data.totales && rango.data.totales.totalUSD) || 0;

        this.costoRealMensual = (mensual.data.periodos || []).map((p) => {
          let costoUSD = 0;
          Object.keys(p.operaciones || {}).forEach((op) => {
            costoUSD += p.operaciones[op].costoUSD;
          });
          return { mes: p.inicio.slice(0, 7), costoUSD };
        });

        // Conteos internos para la columna "Llamadas registradas". Si la tabla
        // no existe todavía (migración phase-14 pendiente), se marca y se
        // muestra la reconciliación solo con datos de AWS.
        try {
          const tipos = OPERACIONES_RECONCILIACION.map((op) => op.tipoInterno);
          const conteos = await Promise.all(
            tipos.map((t) => this.fnConteoLlamadasAws(t))
          );
          const mapa = {};
          tipos.forEach((t, i) => {
            mapa[t] = conteos[i];
          });
          this.conteoLlamadas = mapa;
          this.migracionLlamadasPendiente = false;
        } catch (errConteo) {
          console.warn(
            'reconocimiento_llamadas_aws no disponible (¿migración phase-14 pendiente?):',
            errConteo.message
          );
          this.conteoLlamadas = {};
          this.migracionLlamadasPendiente = true;
        }
      } catch (err) {
        this.costoReal.configurado = true;
        this.costoReal.error =
          (err.response && err.response.data && err.response.data.message) ||
          err.message;
      } finally {
        this.costoReal.cargando = false;
        this.$nextTick(() => this.renderGraficaCostoRealVsEstimado());
      }
    },

    renderGraficaCostoRealVsEstimado() {
      if (this.chartCostoRealVsEstimado) {
        this.chartCostoRealVsEstimado.destroy();
        this.chartCostoRealVsEstimado = null;
      }
      const canvas = this.$refs.canvasCostoRealVsEstimado;
      if (!canvas || !this.tendenciaMensual.length) return;

      // Estimado SOLO Rekognition + Liveness (sin OTP/CloudWatch), para que sea
      // comparable con lo que factura Cost Explorer en este filtro.
      const costoRekognitionPorGestion =
        COSTO_AWS.llamadasRekognitionPorGestion *
          COSTO_AWS.rekognitionImagenPorLlamada +
        COSTO_AWS.faceLivenessPorCheck;
      const realPorMes = {};
      this.costoRealMensual.forEach((m) => {
        realPorMes[m.mes] = m.costoUSD;
      });

      this.chartCostoRealVsEstimado = new Chart(canvas.getContext('2d'), {
        type: 'bar',
        data: {
          labels: this.tendenciaMensual.map((m) => m.mesTexto),
          datasets: [
            {
              label: 'Estimado (dashboard)',
              data: this.tendenciaMensual.map(
                (m) => m.gestionesCompletadas * costoRekognitionPorGestion
              ),
              backgroundColor: COLOR_CATEGORICO.email,
              borderRadius: 4,
              maxBarThickness: 18,
            },
            {
              label: 'Facturado real (Cost Explorer)',
              data: this.tendenciaMensual.map((m) => realPorMes[m.mes] || 0),
              backgroundColor: COLOR_CATEGORICO.sms,
              borderRadius: 4,
              maxBarThickness: 18,
            },
          ],
        },
        options: {
          plugins: {
            legend: {
              position: 'bottom',
              labels: { color: COLOR_TEXTO_SECUNDARIO },
            },
          },
          scales: {
            x: { grid: { display: false }, ticks: { color: COLOR_MUTED } },
            y: {
              beginAtZero: true,
              grid: { color: COLOR_GRID, drawBorder: false },
              ticks: { color: COLOR_MUTED },
            },
          },
        },
      });
    },

    async cargarTodo() {
      await this.cargarKpis();
      this.renderGraficaDona();
      await this.cargarTendencia();
      await this.cargarCostoReal();
    },
  },

  mounted() {
    this.cargarTodo();
  },

  beforeDestroy() {
    if (this.chartDona) this.chartDona.destroy();
    if (this.chartTendenciaGestiones) this.chartTendenciaGestiones.destroy();
    if (this.chartTendenciaCosto) this.chartTendenciaCosto.destroy();
    if (this.chartCostoRealVsEstimado) this.chartCostoRealVsEstimado.destroy();
  },
};
</script>

<style scoped>
.costos-reconocimiento-root {
  --surface-1: #fcfcfb;
  --text-secondary: #52514e;
  --text-muted: #898781;
}
</style>
